import {
  deserializeSuperjson,
  serializeSuperjson,
  SuperJSONResult,
} from '@lib/superjson';
import { isInSet } from '@lib/util/type-guards';
import type { AdminUserUpsertInput } from '@snap-types/actions/create-admin-user';
import {
  adminUserSortFieldSet,
  type AdminUserSortQuery,
  type SearchAdminUsersQueryString,
  type SearchAdminUsersResponse,
} from '@snap-types/actions/search-admin-users';
import type { PaginationParams } from '@snap-types/models/pagination';
import type { UserWithSettings } from '@snap-types/models/user';
import type { SortingState } from '@tanstack/react-table';

import type { API } from '../api';

export class AdminUserService {
  constructor(private api: API) {}

  searchUsers = async (
    search: string,
    { page, perPage }: PaginationParams,
    sort: SortingState = [],
  ) => {
    const query: SearchAdminUsersQueryString = {
      search,
      page,
      perPage,
      sort: this.serializeSort(sort),
    };
    return deserializeSuperjson<SearchAdminUsersResponse>(
      await this.api.get<SuperJSONResult>('/admin/users', query),
    );
  };

  save = async (user: AdminUserUpsertInput) => {
    return deserializeSuperjson<UserWithSettings>(
      await this.api.post<SuperJSONResult>(
        '/admin/users',
        serializeSuperjson(user),
      ),
    );
  };

  private serializeSort(sort: SortingState): AdminUserSortQuery[] {
    const serializedSort = sort?.reduce((acc, cur) => {
      const { id, desc } = cur;

      if (isInSet(adminUserSortFieldSet, id)) {
        acc.push(`${id}:${desc ? 'desc' : 'asc'}`);
      }

      return acc;
    }, [] as AdminUserSortQuery[]);

    return serializedSort;
  }
}
